import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAssemblyType } from "./enums/enumAssemblyType.generated";

// Model Objects

export class RecipeOutputItem extends ModelObject<RecipeOutputItem> {

    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "RecipeOutputItem.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "RecipeOutputItem.UnitOfMeasureId cannot be empty"},
    ];
}


export class RecipeHeader extends ModelObjectWithDirtyCheck<RecipeHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    private _bestBeforeDaysFromStart: number | null = null;
    /**
    * Best Before Days From Start
    */
    public get bestBeforeDaysFromStart(): number | null { return this._bestBeforeDaysFromStart }
    public set bestBeforeDaysFromStart(value: number | null) {
      this._bestBeforeDaysFromStart = value === undefined ? null : value;
    }
    /**
    * Name.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Duration in days this process is planned to take by default.
    * Not Negative
    */
    public plannedDurationDays: number = 0;
    /**
    * Assembly type of this process. : 0 = MaterialProduction, 1 = ProductPackaging, 2 = Maintenance
    */
    public processType: EnumAssemblyType = EnumAssemblyType.materialProduction;
    private _outputItem: RecipeOutputItem | null = null;
    public get outputItem(): RecipeOutputItem | null { return this._outputItem }
    public set outputItem(value: RecipeOutputItem | null) {
      this._outputItem = value === undefined ? null : value;
    }
    private _outputQuantity: number | null = null;
    /**
    * Quantity of output item which is produced by Recipe (null if no output defined). Note that it is now possible for a recipe to know its output quantity even if it does not know the output item (and that in this case the output quantity unit of measure is not known until an actual item is chosen).
    * Not Negative
    */
    public get outputQuantity(): number | null { return this._outputQuantity }
    public set outputQuantity(value: number | null) {
      this._outputQuantity = value === undefined ? null : value;
    }
    /**
    * If true, this recipe scales by duplication, Ie multiple processes are created. If false, this recipe scales by size, Ie a single larger process is created.
    */
    public scalesByDuplication: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.bestBeforeDaysFromStart = jsonSerializedObject.bestBeforeDaysFromStart == null ? null : jsonSerializedObject.bestBeforeDaysFromStart;
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.plannedDurationDays = jsonSerializedObject.plannedDurationDays;
          this.processType = EnumAssemblyType.getByValue(jsonSerializedObject.processType, this.processType);
          this.outputItem = jsonSerializedObject.outputItem == null ? null : new RecipeOutputItem(jsonSerializedObject.outputItem);
          this.outputQuantity = jsonSerializedObject.outputQuantity == null ? null : jsonSerializedObject.outputQuantity;
          this.scalesByDuplication = jsonSerializedObject.scalesByDuplication;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.bestBeforeDaysFromStart = this.bestBeforeDaysFromStart == null ? null : this.bestBeforeDaysFromStart;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.plannedDurationDays = this.plannedDurationDays;
        toRet.processType = this.processType.value;
        toRet.outputItem = this.outputItem == null ? null : this.outputItem?.toJsonSerializedObject() ?? null;
        toRet.outputQuantity = this.outputQuantity == null ? null : this.outputQuantity;
        toRet.scalesByDuplication = this.scalesByDuplication;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeHeader.Id cannot be empty"},
            {field: "bestBeforeDaysFromStart", rule: (v: any) => v == null || v >= -2147483648 || "BestBeforeDaysFromStart is not greater than minimum allowed value: -2147483648"},
            {field: "bestBeforeDaysFromStart", rule: (v: any) => v == null || v <= 2147483647 || "BestBeforeDaysFromStart is above maximum allowed value: 2147483647"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v >= 0 || "PlannedDurationDays is not greater than minimum allowed value: 0"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v <= 2147483647 || "PlannedDurationDays is above maximum allowed value: 2147483647"},
            {field: "outputItem", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class RecipeInstruction extends ModelObject<RecipeInstruction> {

    public id: Guid = Guid.createEmpty();
    /**
    * Parent Recipe Id
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Instruction
    * Not Nullable
    * Max Length : 1000
    */
    public instruction: string = "";
    /**
    * Sequence
    */
    public sequence: number = 0;
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.instruction = jsonSerializedObject.instruction;
          this.sequence = jsonSerializedObject.sequence;
          this.notes = jsonSerializedObject.notes;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.recipeId = this.recipeId.toString();
        toRet.instruction = this.instruction;
        toRet.sequence = this.sequence;
        toRet.notes = this.notes;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeInstruction.Id cannot be empty"},
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeInstruction.RecipeId cannot be empty"},
            {field: "instruction", rule: (v: any) => v == null || v.length <= 1000 || "Instruction has too many characters (1000)"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
    ];
}


export class RecipeVesselRequirement extends ModelObject<RecipeVesselRequirement> {

    public id: Guid = Guid.createEmpty();
    /**
    * Parent Recipe Id
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Required minimum capacity of Vessel in Litres
    * Not Negative
    */
    public capacityLitres: number = 0;
    /**
    * Required Vessel Capability.
    */
    public vesselCapabilityId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.capacityLitres = jsonSerializedObject.capacityLitres;
          this.vesselCapabilityId = Guid.parseOrEmpty(jsonSerializedObject.vesselCapabilityId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.recipeId = this.recipeId.toString();
        toRet.capacityLitres = this.capacityLitres;
        toRet.vesselCapabilityId = this.vesselCapabilityId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirement.Id cannot be empty"},
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirement.RecipeId cannot be empty"},
            {field: "vesselCapabilityId", rule: (v: any) => !v.isEmpty() || "RecipeVesselRequirement.VesselCapabilityId cannot be empty"},
    ];
}


export class RecipeIngredient extends ModelObject<RecipeIngredient> {

    public id: Guid = Guid.createEmpty();
    /**
    * Parent Recipe Id
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Whether this ingredient is fixed to its current quantity regardless of adjustments to process output.If set to true, the ingredient required quantity will not be multiplied when adjusting planned process output quantities.
    */
    public fixedQuantity: boolean = false;
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Quantity of ingredient required.
    * Greater Than : 0
    */
    public quantity: number = 0;
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;
    /**
    * Stock Item Id Being Used.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Enables hop alpha scaling for this ingredient if the item and stock batch have hop alpha values.
    */
    public allowsHopAlpha: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.fixedQuantity = jsonSerializedObject.fixedQuantity;
          this.notes = jsonSerializedObject.notes;
          this.quantity = jsonSerializedObject.quantity;
          this.sequence = jsonSerializedObject.sequence;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.allowsHopAlpha = jsonSerializedObject.allowsHopAlpha;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.recipeId = this.recipeId.toString();
        toRet.fixedQuantity = this.fixedQuantity;
        toRet.notes = this.notes;
        toRet.quantity = this.quantity;
        toRet.sequence = this.sequence;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.allowsHopAlpha = this.allowsHopAlpha;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RecipeIngredient.Id cannot be empty"},
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeIngredient.RecipeId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "quantity", rule: (v: any) => v == null || v > 0 || "Quantity is not greater than minimum allowed value: 0"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "RecipeIngredient.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "RecipeIngredient.UnitOfMeasureId cannot be empty"},
    ];
}


export class Recipe extends ModelObject<Recipe> {

    public header: RecipeHeader = new RecipeHeader();
    public ingredients: (RecipeIngredient)[] = [];
    public instructions: (RecipeInstruction)[] = [];
    public vesselRequirements: (RecipeVesselRequirement)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new RecipeHeader(jsonSerializedObject.header);
          this.ingredients = jsonSerializedObject.ingredients.map((v: any) => new RecipeIngredient(v));
          this.instructions = jsonSerializedObject.instructions.map((v: any) => new RecipeInstruction(v));
          this.vesselRequirements = jsonSerializedObject.vesselRequirements.map((v: any) => new RecipeVesselRequirement(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.ingredients = this.ingredients.map(v => v.toJsonSerializedObject());
        toRet.instructions = this.instructions.map(v => v.toJsonSerializedObject());
        toRet.vesselRequirements = this.vesselRequirements.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "ingredients", rule: (v: any) => !!v || "ingredients is required"},
            {field: "ingredients", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeIngredient[]).reduce((acc: Array<string>, curr: RecipeIngredient) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "instructions", rule: (v: any) => !!v || "instructions is required"},
            {field: "instructions", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeInstruction[]).reduce((acc: Array<string>, curr: RecipeInstruction) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "vesselRequirements", rule: (v: any) => !!v || "vesselRequirements is required"},
            {field: "vesselRequirements", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeVesselRequirement[]).reduce((acc: Array<string>, curr: RecipeVesselRequirement) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class RecipeFilter extends ModelObject<RecipeFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _processTypes: readonly (EnumAssemblyType)[] | null = null;
    /**
    * If provided only matching types are returned
    */
    public get processTypes(): readonly (EnumAssemblyType)[] | null { return this._processTypes }
    public set processTypes(value: readonly (EnumAssemblyType)[] | null) {
      this._processTypes = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.processTypes = jsonSerializedObject.processTypes == null ? null : jsonSerializedObject.processTypes.map((v: any) => EnumAssemblyType.getByValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.processTypes = this.processTypes == null ? null : this.processTypes.map(v => v.value);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredRecipes extends ModelObject<FilteredRecipes> {

    public matchingCount: number = 0;
    public recipes: (Recipe)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.recipes = jsonSerializedObject.recipes.map((v: any) => new Recipe(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.recipes = this.recipes.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "recipes", rule: (v: any) => !!v || "recipes is required"},
            {field: "recipes", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Recipe[]).reduce((acc: Array<string>, curr: Recipe) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredRecipeHeaders extends ModelObject<FilteredRecipeHeaders> {

    public matchingCount: number = 0;
    public recipeHeaders: (RecipeHeader)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.recipeHeaders = jsonSerializedObject.recipeHeaders.map((v: any) => new RecipeHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.recipeHeaders = this.recipeHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "recipeHeaders", rule: (v: any) => !!v || "recipeHeaders is required"},
            {field: "recipeHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeHeader[]).reduce((acc: Array<string>, curr: RecipeHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class RecipeService extends ServiceBase {

  public async getRecipe(recipeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Recipe> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    };
    const extractor = (response:any) => new Recipe(response.recipe);;
    return this.makeJsonRequest("recipe/v2/getRecipe", req, false, extractor, opts);
  };

  public async getRecipes(recipeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Recipe)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIds:recipeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.recipes.map((v: any) => new Recipe(v));;
    return this.makeJsonRequest("recipe/v2/getRecipes", req, false, extractor, opts);
  };

  public async getAllRecipes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Recipe)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.recipes.map((v: any) => new Recipe(v));;
    return this.makeJsonRequest("recipe/v2/getAllRecipes", req, false, extractor, opts);
  };

  public async setRecipeIsHidden(recipeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/setRecipeIsHidden", req, true, extractor, opts);
  };

  public async copyRecipe(recipeId: Guid, newRecipeName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Guid> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    newRecipeName:newRecipeName,
    };
    const extractor = (response:any) => Guid.parseOrEmpty(response.newId);;
    return this.makeJsonRequest("recipe/v2/copyRecipe", req, true, extractor, opts);
  };

  public async getFilteredRecipes(filter: RecipeFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredRecipes> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredRecipes(response.results);;
    return this.makeJsonRequest("recipe/v2/getFilteredRecipes", req, false, extractor, opts);
  };

  public async getRecipeHeader(recipeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<RecipeHeader> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    };
    const extractor = (response:any) => new RecipeHeader(response.recipeHeader);;
    return this.makeJsonRequest("recipe/v2/getRecipeHeader", req, false, extractor, opts);
  };

  public async getRecipeHeaders(recipeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RecipeHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIds:recipeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.recipeHeaders.map((v: any) => new RecipeHeader(v));;
    return this.makeJsonRequest("recipe/v2/getRecipeHeaders", req, false, extractor, opts);
  };

  public async getAllRecipeHeaders(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RecipeHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.recipeHeaders.map((v: any) => new RecipeHeader(v));;
    return this.makeJsonRequest("recipe/v2/getAllRecipeHeaders", req, false, extractor, opts);
  };

  public async saveRecipeHeader(header: RecipeHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeHeader", req, true, extractor, opts);
  };

  public async getFilteredRecipeHeaders(filter: RecipeFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredRecipeHeaders> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredRecipeHeaders(response.results);;
    return this.makeJsonRequest("recipe/v2/getFilteredRecipeHeaders", req, false, extractor, opts);
  };

  public async saveRecipeInstruction(instruction: RecipeInstruction, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    instruction:instruction.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeInstruction", req, true, extractor, opts);
  };

  public async saveRecipeInstructions(instructions: readonly RecipeInstruction[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    instructions:instructions.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeInstructions", req, true, extractor, opts);
  };

  public async hardDeleteRecipeInstruction(recipeInstructionId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeInstructionId:recipeInstructionId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/hardDeleteRecipeInstruction", req, true, extractor, opts);
  };

  public async getIngredientsForRecipes(recipeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RecipeIngredient)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIds:recipeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.ingredients.map((v: any) => new RecipeIngredient(v));;
    return this.makeJsonRequest("recipe/v2/getIngredientsForRecipes", req, false, extractor, opts);
  };

  public async saveRecipeIngredient(ingredient: RecipeIngredient, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ingredient:ingredient.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeIngredient", req, true, extractor, opts);
  };

  public async saveRecipeIngredients(ingredients: readonly RecipeIngredient[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ingredients:ingredients.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeIngredients", req, true, extractor, opts);
  };

  public async hardDeleteRecipeIngredients(recipeIngredientIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIngredientIds:recipeIngredientIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/hardDeleteRecipeIngredients", req, true, extractor, opts);
  };

  public async saveRecipeVesselRequirement(requirement: RecipeVesselRequirement, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    requirement:requirement.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/saveRecipeVesselRequirement", req, true, extractor, opts);
  };

  public async hardDeleteRecipeVesselRequirement(requirementId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    requirementId:requirementId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipe/v2/hardDeleteRecipeVesselRequirement", req, true, extractor, opts);
  };
}

export const recipeService = new RecipeService();